"use client"

import { useState } from "react"
import { API_ENDPOINTS, getApiErrorDetail, useApi } from "@/lib/api"
import { invalidateHubCounts } from "@/lib/hub-counts-refresh"
import {
  parseDocEnvelopes,
  parseProviderConfigData,
  serializeProviderConfigData,
} from "@/lib/global-config"
import {
  CategoryFieldDefaults,
  CompanyMapping,
  GROUP_NAMES,
  InternalOrganizationLookup,
  ProfileTypeLookup,
  ProviderConfigurationData,
} from "@/types/payroll"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Switch } from "@/components/ui/switch"
import { useToast } from "@/hooks/use-toast"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/translations"
import { Loader2, Plus, Trash2 } from "lucide-react"
import type { ProviderDocs } from "./providers-page"
import { CredentialsSection } from "./credentials-section"

interface Props {
  provider: string
  docs: ProviderDocs
  /** The tenant's Worker* profile types, labelled with the code-table Values. */
  profileTypes: ProfileTypeLookup[]
  /** The tenant's internal organizations for the mapping dropdown (empty = lookup unavailable, fields stay free-typed). */
  internalOrganizations: InternalOrganizationLookup[]
  onSaved: () => void
}

export function emptyConfig(provider: string): ProviderConfigurationData {
  return {
    provider,
    isEnabled: false,
    providerEnvironmentName: "sandbox",
    baseUrl: provider === "EmployDrive" ? "https://apihandshake.com/api/v2" : "",
    providerClientId: "",
    companyMappings: [],
    allowedProfileTypes: [],
    migrateOnApproval: true,
    resyncOnChange: false,
    sendTerminations: false,
    reconcileMissedWorkers: false,
    migratePerInternalOrganization: false,
    organizationLastNameSuffix: "Inc",
    includeHourlyRate: false,
    includeNormalHours: false,
    includeDepartment: false,
    includeWorkersCompCode: false,
    fieldDefaults: {
      all: { constantFields: {}, organizationFields: [] },
      individual: { constantFields: {}, organizationFields: [] },
      organization: { constantFields: {}, organizationFields: [] },
    },
  }
}

// ---- Standard values, edited as a simple form (no JSON) ----

interface StandardValueRow {
  name: string
  value: string
  /**
   * The stored value and its display text at load time. While the operator
   * leaves the text untouched, saving re-emits the ORIGINAL value verbatim —
   * so "0042", the string "true", or an object survive a save that only
   * changed something else. Re-typing happens only for text the operator
   * actually edited.
   */
  original?: unknown
  originalText?: string
}

type AudienceKey = "all" | "individual" | "organization"

const AUDIENCES: { key: AudienceKey; labelKey: TranslationKey; hintKey: TranslationKey }[] = [
  { key: "all", labelKey: "providers.audience.all.label", hintKey: "providers.audience.all.hint" },
  { key: "individual", labelKey: "providers.audience.individual.label", hintKey: "providers.audience.individual.hint" },
  { key: "organization", labelKey: "providers.audience.organization.label", hintKey: "providers.audience.organization.hint" },
]

function valueText(value: unknown): string {
  if (value === null || value === undefined) return ""
  // Objects/arrays render as editable JSON, not "[object Object]".
  return typeof value === "string" ? value : JSON.stringify(value)
}

function toRows(defaults: CategoryFieldDefaults): StandardValueRow[] {
  return Object.entries(defaults.constantFields ?? {}).map(([name, value]) => {
    const text = valueText(value)
    return { name, value: text, original: value, originalText: text }
  })
}

/**
 * "true"/"false" become real booleans, canonical numbers become numbers, and
 * JSON objects/arrays parse — so the provider receives properly typed JSON.
 * Non-canonical numerics like "0042" stay strings (Number() would drop the
 * leading zero).
 */
function typedValue(text: string): unknown {
  const trimmed = text.trim()
  if (/^(true|false)$/i.test(trimmed)) return trimmed.toLowerCase() === "true"
  if (/^-?\d+(\.\d+)?$/.test(trimmed) && String(Number(trimmed)) === trimmed) return Number(trimmed)
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    try {
      return JSON.parse(trimmed)
    } catch {
      return text
    }
  }
  return text
}

function fromRows(rows: StandardValueRow[], orgFields: CategoryFieldDefaults["organizationFields"]): CategoryFieldDefaults {
  const constantFields: Record<string, unknown> = {}
  for (const row of rows) {
    if (!row.name.trim()) continue
    // Untouched text = re-emit the stored value verbatim (lossless round-trip);
    // only text the operator actually edited goes through re-typing.
    constantFields[row.name.trim()] =
      row.originalText !== undefined && row.value === row.originalText ? row.original : typedValue(row.value)
  }
  return { constantFields, organizationFields: orgFields.filter((f) => f.title.trim() || f.value.trim()) }
}

export function ProviderEditor({ provider, docs, profileTypes, internalOrganizations, onSaved }: Props) {
  const { adminApi, payrollApi } = useApi()
  const { toast } = useToast()
  const { t } = useTranslation()

  const [config, setConfig] = useState<ProviderConfigurationData>(docs.configDoc?.data ?? emptyConfig(provider))
  const [standardRows, setStandardRows] = useState<Record<AudienceKey, StandardValueRow[]>>(() => ({
    all: toRows((docs.configDoc?.data ?? emptyConfig(provider)).fieldDefaults.all),
    individual: toRows((docs.configDoc?.data ?? emptyConfig(provider)).fieldDefaults.individual),
    organization: toRows((docs.configDoc?.data ?? emptyConfig(provider)).fieldDefaults.organization),
  }))
  const [orgAssignments, setOrgAssignments] = useState<Record<AudienceKey, CategoryFieldDefaults["organizationFields"]>>(() => ({
    all: (docs.configDoc?.data ?? emptyConfig(provider)).fieldDefaults.all.organizationFields,
    individual: (docs.configDoc?.data ?? emptyConfig(provider)).fieldDefaults.individual.organizationFields,
    organization: (docs.configDoc?.data ?? emptyConfig(provider)).fieldDefaults.organization.organizationFields,
  }))
  const [isOffered, setIsOffered] = useState(docs.formatDoc != null)
  const [isDefaultFormat, setIsDefaultFormat] = useState(docs.formatDoc?.isDefault ?? false)
  const [isSaving, setIsSaving] = useState(false)
  const [isTesting, setIsTesting] = useState(false)
  const [testResult, setTestResult] = useState<{ success: boolean; error?: string } | null>(null)

  const update = (patch: Partial<ProviderConfigurationData>) => setConfig((c) => ({ ...c, ...patch }))

  const updateMapping = (index: number, patch: Partial<CompanyMapping>) =>
    setConfig((c) => ({
      ...c,
      companyMappings: c.companyMappings.map((m, i) => (i === index ? { ...m, ...patch } : m)),
    }))

  const save = async () => {
    if (config.isEnabled && !config.providerEnvironmentName.trim()) {
      toast({ variant: "destructive", title: t("providers.toast.chooseEnvironment") })
      return
    }

    setIsSaving(true)
    try {
      const fullConfig: ProviderConfigurationData = {
        ...config,
        fieldDefaults: {
          all: fromRows(standardRows.all, orgAssignments.all),
          individual: fromRows(standardRows.individual, orgAssignments.individual),
          organization: fromRows(standardRows.organization, orgAssignments.organization),
        },
      }

      // Ask the server which documents exist RIGHT NOW — deciding create vs
      // update from possibly-stale props once created a duplicate document.
      const configPath = API_ENDPOINTS.ADMIN.CONFIG_BY_GROUP(GROUP_NAMES.providerConfiguration)
      const existingConfigs = parseDocEnvelopes(await adminApi.get<unknown>(configPath))
      // Fallback "" (matching the load path), NEVER the provider being saved —
      // that would make a malformed doc with no Provider field match every
      // provider and get silently clobbered.
      const existingConfig = existingConfigs.find(
        (d) => parseProviderConfigData(d.data, "").provider.toLowerCase() === provider.toLowerCase(),
      )

      // Updates spread the server's own envelope (PartitionKey and all) and
      // replace only Data — Cosmos rejects a replace whose PartitionKey is
      // missing. Creates send the minimal envelope; the server fills the rest.
      if (existingConfig?.id) {
        await adminApi.put(configPath, { ...existingConfig.envelope, Data: serializeProviderConfigData(fullConfig) })
      } else {
        await adminApi.post(configPath, {
          documentType: "Configuration",
          groupName: GROUP_NAMES.providerConfiguration,
          data: serializeProviderConfigData(fullConfig),
        })
      }

      // Payment file format document (also drives payment-file generation).
      if (isOffered) {
        const formatPath = API_ENDPOINTS.ADMIN.CONFIG_BY_GROUP(GROUP_NAMES.paymentFileFormatType)
        const existingFormats = parseDocEnvelopes(await adminApi.get<unknown>(formatPath))
        const existingFormat = existingFormats.find((d) => {
          const value = (d.data as Record<string, unknown> | null)?.Value ?? (d.data as Record<string, unknown> | null)?.value
          return typeof value === "string" && value.toLowerCase() === provider.toLowerCase()
        })
        const formatData = { Value: provider, Text: provider, IsDefault: isDefaultFormat }
        if (existingFormat?.id) {
          await adminApi.put(formatPath, { ...existingFormat.envelope, Data: formatData })
        } else {
          await adminApi.post(formatPath, {
            documentType: "Configuration",
            groupName: GROUP_NAMES.paymentFileFormatType,
            data: formatData,
          })
        }
      }

      // The hub card names the live provider and its environment.
      invalidateHubCounts()
      onSaved()
    } catch (err) {
      toast({
        variant: "destructive",
        title: t("providers.toast.saveFailed"),
        description: getApiErrorDetail(err) ?? String(err),
      })
    } finally {
      setIsSaving(false)
    }
  }

  const testConnection = async () => {
    setIsTesting(true)
    setTestResult(null)
    try {
      const result = await payrollApi.post<{ success: boolean; error?: string }>(
        API_ENDPOINTS.PAYROLL.TEST_CONNECTION(provider),
        {},
      )
      setTestResult(result)
    } catch (err) {
      setTestResult({ success: false, error: getApiErrorDetail(err) ?? String(err) })
    } finally {
      setIsTesting(false)
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle>{provider}</CardTitle>
          <CardDescription>{t("providers.headerDescription", { provider })}</CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => void testConnection()} disabled={isTesting}>
            {isTesting && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            {t("providers.testConnection")}
          </Button>
          <Button size="sm" onClick={() => void save()} disabled={isSaving}>
            {isSaving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            {t("common.save")}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {testResult && (
          <div
            className={
              testResult.success
                ? "rounded-lg border border-[var(--border-success)] bg-[var(--bg-success)] p-3 text-sm text-[var(--gw-text-success)]"
                : "rounded-lg border border-[var(--border-error)] bg-[var(--bg-error)] p-3 text-sm text-[var(--gw-text-error)]"
            }
          >
            {testResult.success ? t("providers.testSuccess", { provider }) : testResult.error}
          </div>
        )}

        {/* On/off, environment, address, client number — one aligned row:
            every column is label / control / hint so the controls line up. */}
        <div className="flex flex-wrap items-start gap-6">
          <div className="space-y-1">
            <Label htmlFor="provider-enabled">{t("providers.sendingLabel")}</Label>
            <div className="flex h-10 items-center gap-2">
              <Switch id="provider-enabled" checked={config.isEnabled} onCheckedChange={(v) => update({ isEnabled: v === true })} />
              <span className="text-sm">{t(config.isEnabled ? "common.on" : "common.off")}</span>
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="provider-env">{t("providers.sendTo")}</Label>
            <Select value={config.providerEnvironmentName} onValueChange={(v) => update({ providerEnvironmentName: v })}>
              <SelectTrigger id="provider-env" className="w-[190px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sandbox">{t("common.env.sandbox")}</SelectItem>
                <SelectItem value="production">{t("common.env.production")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[260px] flex-1 space-y-1">
            <Label htmlFor="provider-base-url">{t("providers.baseUrl")}</Label>
            <Input
              id="provider-base-url"
              value={config.baseUrl}
              onChange={(e) => update({ baseUrl: e.target.value })}
              placeholder="https://…"
            />
            <p className="text-xs text-muted-foreground">{t("providers.baseUrlHint", { provider })}</p>
          </div>
          <div className="w-48 space-y-1">
            <Label htmlFor="provider-client-id">{t("providers.clientIdLabel", { provider })}</Label>
            <Input
              id="provider-client-id"
              value={config.providerClientId ?? ""}
              onChange={(e) => update({ providerClientId: e.target.value })}
              placeholder={t("providers.clientIdPlaceholder")}
            />
            <p className="text-xs text-muted-foreground">{t("providers.clientIdHint", { provider })}</p>
          </div>
        </div>

        <Separator />

        {/* Payment file format */}
        <section className="space-y-2">
          <h3 className="text-sm font-semibold">{t("providers.paymentFormat.title")}</h3>
          <p className="text-xs text-muted-foreground">{t("providers.paymentFormat.hint", { provider })}</p>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Checkbox id="offered" checked={isOffered} onCheckedChange={(v) => setIsOffered(v === true)} />
              <Label htmlFor="offered">{t("providers.paymentFormat.available")}</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="default-format"
                checked={isDefaultFormat}
                disabled={!isOffered}
                onCheckedChange={(v) => setIsDefaultFormat(v === true)}
              />
              <Label htmlFor="default-format">{t("providers.paymentFormat.default")}</Label>
            </div>
          </div>
        </section>

        <Separator />

        <CredentialsSection provider={provider} credential={docs.credential} />

        <Separator />

        {/* Your companies */}
        <section className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">{t("providers.orgs.title")}</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setConfig((c) => ({
                  ...c,
                  companyMappings: [
                    ...c.companyMappings,
                    { internalOrganizationId: 0, internalOrganizationCode: "", internalOrganizationName: "", providerClientId: "", legalCode: "", payGroup: "", isExcluded: false },
                  ],
                }))
              }
            >
              <Plus className="mr-1 h-4 w-4" /> {t("providers.orgs.add")}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">{t("providers.orgs.hint", { provider })}</p>
          {config.companyMappings.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("providers.orgs.empty")}</p>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-[80px_1fr_1fr_120px_90px_110px_100px_36px] gap-2 text-xs font-medium text-muted-foreground">
                <span>{t("providers.orgs.header.fboId")}</span>
                <span>{t("providers.orgs.header.orgCode")}</span>
                <span>{t("providers.orgs.header.orgName")}</span>
                <span>{t("providers.orgs.header.clientId")}</span>
                <span>{t("field.legalCode")}</span>
                <span>{t("field.payGroup")}</span>
                <span />
                <span />
              </div>
              {config.companyMappings.map((mapping, index) => (
                <div key={index} className="grid grid-cols-[80px_1fr_1fr_120px_90px_110px_100px_36px] items-center gap-2">
                  <Input
                    aria-label={t("providers.orgs.aria.fboId")}
                    placeholder={t("providers.orgs.placeholder.fboId")}
                    type="number"
                    value={mapping.internalOrganizationId || ""}
                    onChange={(e) => updateMapping(index, { internalOrganizationId: Number(e.target.value) || 0 })}
                  />
                  <Input
                    aria-label={t("field.internalOrganizationCode")}
                    placeholder={t("providers.orgs.placeholder.orgCode")}
                    value={mapping.internalOrganizationCode}
                    onChange={(e) => updateMapping(index, { internalOrganizationCode: e.target.value })}
                  />
                  {internalOrganizations.length > 0 ? (
                    // Picking an organization fills the FBO id and code too.
                    <Select
                      value={mapping.internalOrganizationId > 0 ? String(mapping.internalOrganizationId) : ""}
                      onValueChange={(v) => {
                        const org = internalOrganizations.find((o) => String(o.id) === v)
                        if (org) {
                          updateMapping(index, {
                            internalOrganizationId: org.id,
                            internalOrganizationCode: org.code,
                            internalOrganizationName: org.name,
                          })
                        }
                      }}
                    >
                      <SelectTrigger aria-label={t("providers.orgs.header.orgName")}>
                        <SelectValue placeholder={t("common.chooseOrganization")} />
                      </SelectTrigger>
                      <SelectContent>
                        {internalOrganizations.map((o) => (
                          <SelectItem key={o.id} value={String(o.id)}>
                            {o.name}
                          </SelectItem>
                        ))}
                        {/* A saved organization that has since left the lookup
                            (soft-deleted, or its internal role removed) must
                            stay visible so the operator can see and repoint it
                            — mirrors the mappings tab's stale-value entry. */}
                        {mapping.internalOrganizationId > 0 &&
                          !internalOrganizations.some((o) => o.id === mapping.internalOrganizationId) && (
                            <SelectItem value={String(mapping.internalOrganizationId)}>
                              {t("common.notInCurrentListValue", {
                                value: mapping.internalOrganizationName || `#${mapping.internalOrganizationId}`,
                              })}
                            </SelectItem>
                          )}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      aria-label={t("providers.orgs.header.orgName")}
                      placeholder={t("providers.orgs.header.orgName")}
                      value={mapping.internalOrganizationName}
                      onChange={(e) => updateMapping(index, { internalOrganizationName: e.target.value })}
                    />
                  )}
                  <Input
                    aria-label={t("providers.orgs.aria.clientIdOverride")}
                    placeholder={t("providers.orgs.placeholder.usesDefault")}
                    disabled={mapping.isExcluded}
                    value={mapping.providerClientId ?? ""}
                    onChange={(e) => updateMapping(index, { providerClientId: e.target.value })}
                  />
                  <Input
                    aria-label={t("field.legalCode")}
                    placeholder={t("providers.orgs.placeholder.legalCode")}
                    disabled={mapping.isExcluded}
                    value={mapping.legalCode ?? ""}
                    onChange={(e) => updateMapping(index, { legalCode: e.target.value })}
                  />
                  <Input
                    aria-label={t("field.payGroup")}
                    placeholder={t("field.payGroup")}
                    disabled={mapping.isExcluded}
                    value={mapping.payGroup ?? ""}
                    onChange={(e) => updateMapping(index, { payGroup: e.target.value })}
                  />
                  <div className="flex items-center gap-1">
                    <Checkbox
                      id={`excluded-${index}`}
                      checked={mapping.isExcluded}
                      onCheckedChange={(v) => updateMapping(index, { isExcluded: v === true })}
                    />
                    <Label htmlFor={`excluded-${index}`} className="text-xs">
                      {t("providers.orgs.dontSend")}
                    </Label>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t("providers.orgs.aria.remove")}
                    onClick={() =>
                      setConfig((c) => ({ ...c, companyMappings: c.companyMappings.filter((_, i) => i !== index) }))
                    }
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </section>

        <Separator />

        {/* Which workers are sent */}
        <section className="space-y-2">
          <h3 className="text-sm font-semibold">{t("providers.types.title")}</h3>
          <p className="text-xs text-muted-foreground">{t("providers.types.hint", { provider })}</p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {/* Labels are the tenant's own code-table Values (e.g. "W2 Worker").
                Codes that are ALLOWED but missing from the lookup still render —
                hiding them would leave a type active with no way to see or
                untick it. The suffix states only what the screen knows (absence
                from its list), not why: on the degraded fallback path a
                perfectly live type can be missing too. */}
            {[
              ...profileTypes,
              ...config.allowedProfileTypes
                .filter((code) => !profileTypes.some((pt) => pt.code === code))
                .map((code) => ({ code, value: t("providers.types.notInList", { code }) })),
            ].map((type) => {
              const checked = config.allowedProfileTypes.includes(type.code)
              return (
                <div key={type.code} className="flex items-center gap-2">
                  <Checkbox
                    id={`ptype-${type.code}`}
                    checked={checked}
                    onCheckedChange={(v) =>
                      update({
                        allowedProfileTypes:
                          v === true
                            ? [...config.allowedProfileTypes, type.code]
                            : config.allowedProfileTypes.filter((c) => c !== type.code),
                      })
                    }
                  />
                  <Label htmlFor={`ptype-${type.code}`} className="text-sm" title={type.code}>
                    {type.value}
                  </Label>
                </div>
              )
            })}
          </div>
        </section>

        <Separator />

        {/* How workers are sent */}
        <section className="space-y-2">
          <h3 className="text-sm font-semibold">{t("providers.how.title")}</h3>
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-2">
              <Checkbox
                id="multi-org"
                checked={config.migratePerInternalOrganization}
                onCheckedChange={(v) => update({ migratePerInternalOrganization: v === true })}
              />
              <Label htmlFor="multi-org" className="text-sm">
                {t("providers.how.perOrg")}
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="org-suffix" className="text-sm">
                {t("providers.how.c2cLastName")}
              </Label>
              <Input
                id="org-suffix"
                className="w-[110px]"
                value={config.organizationLastNameSuffix}
                onChange={(e) => update({ organizationLastNameSuffix: e.target.value })}
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">{t("providers.how.hint", { provider })}</p>
        </section>

        <Separator />

        {/* Extra details from the work order */}
        <section className="space-y-2">
          <h3 className="text-sm font-semibold">{t("providers.extras.title")}</h3>
          <p className="text-xs text-muted-foreground">{t("providers.extras.hint", { provider })}</p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <div className="flex items-center gap-2">
              <Checkbox id="inc-rate" checked={config.includeHourlyRate} onCheckedChange={(v) => update({ includeHourlyRate: v === true })} />
              <Label htmlFor="inc-rate" className="text-sm">{t("field.hourlyRate")}</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="inc-hours" checked={config.includeNormalHours} onCheckedChange={(v) => update({ includeNormalHours: v === true })} />
              <Label htmlFor="inc-hours" className="text-sm">{t("field.normalHours")}</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="inc-dept" checked={config.includeDepartment} onCheckedChange={(v) => update({ includeDepartment: v === true })} />
              <Label htmlFor="inc-dept" className="text-sm">{t("field.department")}</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="inc-wc" checked={config.includeWorkersCompCode} onCheckedChange={(v) => update({ includeWorkersCompCode: v === true })} />
              <Label htmlFor="inc-wc" className="text-sm">{t("field.workersCompCode")}</Label>
            </div>
          </div>
        </section>

        <Separator />

        {/* Standard values */}
        <section className="space-y-3">
          <h3 className="text-sm font-semibold">{t("field.additionalFields")}</h3>
          <p className="text-xs text-muted-foreground">{t("providers.standard.hint", { provider })}</p>
          {AUDIENCES.map((audience) => (
            <div key={audience.key} className="rounded-md border p-3">
              <div className="mb-2">
                <p className="text-sm font-medium">{t(audience.labelKey)}</p>
                <p className="text-xs text-muted-foreground">{t(audience.hintKey)}</p>
              </div>
              <div className="space-y-2">
                {standardRows[audience.key].map((row, index) => (
                  <div key={index} className="grid grid-cols-[1fr_1fr_36px] items-center gap-2">
                    <Input
                      aria-label={t("providers.standard.aria.fieldName", { audience: t(audience.labelKey) })}
                      placeholder={t("providers.standard.placeholder.name")}
                      value={row.name}
                      onChange={(e) =>
                        setStandardRows((r) => ({
                          ...r,
                          [audience.key]: r[audience.key].map((x, i) => (i === index ? { ...x, name: e.target.value } : x)),
                        }))
                      }
                    />
                    <Input
                      aria-label={t("providers.standard.aria.fieldValue", { audience: t(audience.labelKey) })}
                      placeholder={t("providers.standard.placeholder.value")}
                      value={row.value}
                      onChange={(e) =>
                        setStandardRows((r) => ({
                          ...r,
                          [audience.key]: r[audience.key].map((x, i) => (i === index ? { ...x, value: e.target.value } : x)),
                        }))
                      }
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={t("providers.standard.removeValue")}
                      onClick={() =>
                        setStandardRows((r) => ({ ...r, [audience.key]: r[audience.key].filter((_, i) => i !== index) }))
                      }
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <div className="flex items-center gap-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setStandardRows((r) => ({ ...r, [audience.key]: [...r[audience.key], { name: "", value: "" }] }))
                    }
                  >
                    <Plus className="mr-1 h-4 w-4" /> {t("providers.standard.addValue")}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setOrgAssignments((o) => ({
                        ...o,
                        [audience.key]: [...o[audience.key], { title: "", value: "", valueDescription: "" }],
                      }))
                    }
                  >
                    <Plus className="mr-1 h-4 w-4" /> {t("providers.standard.addOrgAssignment")}
                  </Button>
                </div>
                {orgAssignments[audience.key].length > 0 && (
                  <div className="space-y-2 border-t pt-2">
                    <p className="text-xs text-muted-foreground">
                      {t("providers.standard.orgAssignmentsHint", { provider })}
                    </p>
                    {orgAssignments[audience.key].map((field, index) => (
                      <div key={index} className="grid grid-cols-[1fr_1fr_1fr_36px] items-center gap-2">
                        <Input
                          aria-label={t("providers.standard.aria.assignmentTitle")}
                          placeholder={t("providers.standard.placeholder.assignmentTitle")}
                          value={field.title}
                          onChange={(e) =>
                            setOrgAssignments((o) => ({
                              ...o,
                              [audience.key]: o[audience.key].map((x, i) => (i === index ? { ...x, title: e.target.value } : x)),
                            }))
                          }
                        />
                        <Input
                          aria-label={t("providers.standard.aria.assignmentValue")}
                          placeholder={t("providers.standard.placeholder.assignmentValue")}
                          value={field.value}
                          onChange={(e) =>
                            setOrgAssignments((o) => ({
                              ...o,
                              [audience.key]: o[audience.key].map((x, i) => (i === index ? { ...x, value: e.target.value } : x)),
                            }))
                          }
                        />
                        <Input
                          aria-label={t("providers.standard.aria.assignmentNote")}
                          placeholder={t("mappings.header.note")}
                          value={field.valueDescription ?? ""}
                          onChange={(e) =>
                            setOrgAssignments((o) => ({
                              ...o,
                              [audience.key]: o[audience.key].map((x, i) =>
                                i === index ? { ...x, valueDescription: e.target.value } : x,
                              ),
                            }))
                          }
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={t("providers.standard.removeAssignment")}
                          onClick={() =>
                            setOrgAssignments((o) => ({ ...o, [audience.key]: o[audience.key].filter((_, i) => i !== index) }))
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </section>

        <Separator />

        {/* When workers are sent automatically */}
        <section className="space-y-2">
          <h3 className="text-sm font-semibold">{t("providers.triggers.title")}</h3>
          <div className="flex flex-wrap gap-6">
            <div className="flex items-center gap-2">
              <Checkbox
                id="trigger-approval"
                checked={config.migrateOnApproval}
                onCheckedChange={(v) => update({ migrateOnApproval: v === true })}
              />
              <Label htmlFor="trigger-approval">{t("providers.triggers.onApproval")}</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="trigger-resync"
                checked={config.resyncOnChange}
                onCheckedChange={(v) => update({ resyncOnChange: v === true })}
              />
              <Label htmlFor="trigger-resync">{t("providers.triggers.onChange")}</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="trigger-terminations"
                checked={config.sendTerminations}
                onCheckedChange={(v) => update({ sendTerminations: v === true })}
              />
              <Label htmlFor="trigger-terminations">{t("providers.triggers.onTermination")}</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="trigger-reconcile"
                checked={config.reconcileMissedWorkers}
                onCheckedChange={(v) => update({ reconcileMissedWorkers: v === true })}
              />
              <Label htmlFor="trigger-reconcile">{t("providers.triggers.reconcile")}</Label>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">{t("providers.triggers.hint")}</p>
        </section>
      </CardContent>
    </Card>
  )
}
